const PAYMENT_MODAL_SELECTOR = ".cash-sale-modal";
const CASH_INPUT_SELECTOR = ".cash-sale-modal__field input[type='number']";
const TENDER_TOOLS_SELECTOR = ".cash-sale-modal__tender-tools";
const QUICK_BUTTON_SELECTOR = ".cash-sale-modal__tender-quick-button";
const SELECTED_METHOD_SELECTOR = ".cash-sale-modal__method--selected";
const PRIMARY_ACTION_SELECTOR = ".cash-sale-modal__actions .cash-sale-modal__primary";
const CASH_ROW_CLASS = "cash-sale-modal__row";
const PRESENTATION_ATTRIBUTE = "data-payment-v122";

function currencyPrefix(modal: HTMLElement) {
  const total = modal.querySelector<HTMLElement>(".cash-sale-modal__total strong")?.textContent?.trim() ?? "";
  return total.match(/^[^0-9\-]+/)?.[0]?.trim() ?? "";
}

function compactTenderValue(modal: HTMLElement, raw: string) {
  const value = Number(raw);
  if (!Number.isFinite(value)) return raw;
  const fractionDigits = Number.isInteger(value) ? 0 : 2;
  return `${currencyPrefix(modal)}${new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)}`;
}

function directCashChangeRow(modal: HTMLElement) {
  return Array.from(modal.children).find((child): child is HTMLElement => (
    child instanceof HTMLElement
    && child.classList.contains(CASH_ROW_CLASS)
    && (child.textContent ?? "").includes("เงินทอน")
  )) ?? null;
}

function polishQuickTender(modal: HTMLElement) {
  modal.querySelectorAll<HTMLButtonElement>(QUICK_BUTTON_SELECTOR).forEach((button) => {
    const strong = button.querySelector("strong");
    const rawValue = button.dataset.tenderValue;
    if (!strong || rawValue == null) return;

    const wasExact = strong.textContent?.trim().startsWith("พอดี ")
      || button.querySelector(".cash-sale-modal__tender-quick-tag") != null;

    button.querySelectorAll("span:not(.cash-sale-modal__tender-quick-tag)").forEach((node) => node.remove());

    if (wasExact) {
      let tag = button.querySelector<HTMLElement>(".cash-sale-modal__tender-quick-tag");
      if (!tag) {
        tag = document.createElement("span");
        tag.className = "cash-sale-modal__tender-quick-tag";
        button.insertBefore(tag, strong);
      }
      if (tag.textContent !== "พอดี") tag.textContent = "พอดี";
    } else {
      button.querySelector(".cash-sale-modal__tender-quick-tag")?.remove();
    }

    const display = compactTenderValue(modal, rawValue);
    if (strong.textContent !== display) strong.textContent = display;
  });
}

function refreshCashSummary(modal: HTMLElement, input: HTMLInputElement) {
  input.placeholder = "—";
  input.setAttribute("aria-label", "รับเงินสด");

  const changeRow = directCashChangeRow(modal);
  if (!changeRow) return;
  const changeValue = changeRow.querySelector("strong");
  if (!input.value.trim() && changeValue && changeValue.textContent !== "—") {
    changeValue.textContent = "—";
  }
}

function removeStaleCashSurface(modal: HTMLElement) {
  modal.querySelectorAll(TENDER_TOOLS_SELECTOR).forEach((surface) => surface.remove());
}

function polishNonCashSurface(modal: HTMLElement) {
  removeStaleCashSurface(modal);

  const selectedMethod = modal.querySelector(SELECTED_METHOD_SELECTOR);
  if (!selectedMethod) return;

  const primary = modal.querySelector<HTMLButtonElement>(PRIMARY_ACTION_SELECTOR);
  const current = primary?.textContent?.trim() ?? "";
  const prefix = "ได้รับเงินแล้ว · บันทึก ";
  if (primary && current.startsWith(prefix)) {
    const amount = current.slice(prefix.length).trim();
    const next = `ยืนยันรับโอน ${amount}`;
    if (primary.textContent !== next) primary.textContent = next;
  }
}

function refreshPaymentV122Presentation() {
  document.querySelectorAll<HTMLElement>(PAYMENT_MODAL_SELECTOR).forEach((modal) => {
    if (!modal.hasAttribute(PRESENTATION_ATTRIBUTE)) modal.setAttribute(PRESENTATION_ATTRIBUTE, "true");

    const input = modal.querySelector<HTMLInputElement>(CASH_INPUT_SELECTOR);
    if (input) {
      refreshCashSummary(modal, input);
      polishQuickTender(modal);
      return;
    }

    polishNonCashSurface(modal);
  });
}

/**
 * Presentation-only refinement for MORE_POS_TERMINAL_SPEC v1.22.
 *
 * Payment facts, readiness, Receiving Destination authority, Cash tender state,
 * Local Commit, Durable Outbox and Canonical reconciliation remain owned by the
 * existing Commerce/Payment runtime. This adapter only removes stale Cash UI when
 * switching method, simplifies Quick Tender labels, and polishes read-only payment
 * presentation for the current checkout surface.
 */
export function registerPaymentSurfaceV122Presentation() {
  if (typeof document === "undefined" || !document.body || typeof MutationObserver === "undefined") {
    return () => undefined;
  }

  const observer = new MutationObserver(refreshPaymentV122Presentation);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["class"],
  });
  refreshPaymentV122Presentation();

  return () => {
    observer.disconnect();
    document.querySelectorAll<HTMLElement>(PAYMENT_MODAL_SELECTOR).forEach((modal) => {
      modal.removeAttribute(PRESENTATION_ATTRIBUTE);
    });
  };
}
